'use client';

/**
 * Booking Inquiry Form
 *
 * Structured prompt opened by `MessageComposer` when the inbox is
 * entered with `?intent=booking` (set by the premium-dancer contact
 * rail at `dancer-contact-rail.tsx:102-105`) on a conversation with
 * zero prior messages.
 *
 * On submit it formats the five fields into a markdown body and
 * forwards a `metadata: { intent_type: 'booking', booking_payload }`
 * payload that downstream consumers read:
 *   - `MessageList` branches to `BookingInquiryCard` for rendering
 *   - Phase 3: `ConversationList` shows a "Booking" badge
 *
 * Phase 2 of:
 *   docs/wip/active/WIP_wiring_inbox_intent_booking_template_2026_05_13.md
 */
import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@kit/ui/button';
import { DateField } from '@kit/ui/date-field';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@kit/ui/sheet';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';

import { BookingInquirySchema } from './booking-inquiry.schema';

export type BookingInquiryPayload = z.infer<typeof BookingInquirySchema>;

/**
 * Serialized shape persisted to `messages.metadata.booking_payload`.
 * Identical to the schema's parsed shape since dates are now plain
 * strings — no Date coercion. Kept as a separate alias so call sites
 * read clearly and downstream consumers (`BookingInquiryCard`) can
 * import the shape without pulling the schema.
 *
 * Declared as a `type` (not an `interface`) so it conforms structurally
 * to the recursive `Json` type used by `SendMessageParams.metadata` —
 * interfaces without an index signature fail that assignability check.
 */
export type BookingInquirySerialized = {
  subject: string;
  date_range_start: string;
  date_range_end: string;
  production: string;
  compensation_range: string;
  notes: string;
};

export function serializeBookingInquiry(
  values: BookingInquiryPayload,
): BookingInquirySerialized {
  return {
    subject: values.subject.trim(),
    date_range_start: values.date_range_start,
    date_range_end: values.date_range_end,
    production: values.production.trim(),
    compensation_range: values.compensation_range.trim(),
    notes: (values.notes ?? '').trim(),
  };
}

/**
 * Format the structured form into a readable markdown body for the
 * message row. Receivers without a card renderer still see something
 * intelligible; receivers WITH the card renderer ignore the body and
 * render from `metadata.booking_payload` instead.
 */
export function formatBookingInquiryBody(
  payload: BookingInquirySerialized,
): string {
  const lines: string[] = ['**Booking inquiry**', ''];
  lines.push(`Subject: ${payload.subject}`);
  lines.push(`Dates: ${payload.date_range_start} → ${payload.date_range_end}`);
  lines.push(`Production: ${payload.production}`);
  lines.push(`Compensation: ${payload.compensation_range}`);
  if (payload.notes) {
    lines.push('', payload.notes);
  }
  return lines.join('\n');
}

interface BookingInquiryFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: BookingInquirySerialized) => Promise<void> | void; // i18n-ignore TS generic return type, not user-facing copy
}

export function BookingInquiryForm({
  open,
  onOpenChange,
  onSubmit,
}: BookingInquiryFormProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: zodResolver(BookingInquirySchema),
    defaultValues: {
      subject: '',
      date_range_start: '',
      date_range_end: '',
      production: '',
      compensation_range: '',
      notes: '',
    },
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    setIsSubmitting(true);
    try {
      await onSubmit(serializeBookingInquiry(values));
      form.reset();
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>
            <Trans i18nKey="chat:booking.form.title" />
          </SheetTitle>
          <SheetDescription>
            <Trans i18nKey="chat:booking.form.description" />
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="chat:booking.form.subject" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('chat:booking.form.subjectPlaceholder')}
                      maxLength={200}
                      autoFocus
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="date_range_start"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Trans i18nKey="chat:booking.form.startDate" />
                    </FormLabel>
                    <FormControl>
                      <DateField
                        name={field.name}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="date_range_end"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Trans i18nKey="chat:booking.form.endDate" />
                    </FormLabel>
                    <FormControl>
                      <DateField
                        name={field.name}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="production"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="chat:booking.form.production" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('chat:booking.form.productionPlaceholder')}
                      maxLength={200}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="compensation_range"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="chat:booking.form.compensation" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(
                        'chat:booking.form.compensationPlaceholder',
                      )}
                      maxLength={100}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="chat:booking.form.notes" />
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={4}
                      maxLength={2_000}
                      placeholder={t('chat:booking.form.notesPlaceholder')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SheetFooter className="flex-row justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                <Trans i18nKey="chat:booking.form.cancel" />
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Trans i18nKey="chat:booking.form.sending" />
                ) : (
                  <Trans i18nKey="chat:booking.form.submit" />
                )}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
